import BaseUISystem from '../../BaseUISystem';
import Game from 'Game';
import BaseMenu from '../BaseMenu';
import { LevelCompletePopupLayout } from 'Layouts/LevelPopup/LevelCompletePopupLayout';
import TransformComponent from 'Component/Transform';
import { SideMenu } from './SideMenu';
import { trackFtuxOnHide, trackLevelEndShown } from './LevelPopupTrack';
import TextComponent from 'Component/Render/Text';
import { applyDefaultButtonAnimation } from '../../UIHelper';
import { GameObjectEvent } from 'GameObject/GameObject';
import { IGameState } from 'State/Game/IGameState';
import { GameActions } from 'State/Game/GameActions';
import { LevelStatus } from 'State/Level/LevelStatus';
import Renderable from 'Component/Renderable';
import { sleep } from 'Utility/async';
import { Anim } from 'System/Animation/AnimationHelper';
import { Easing } from '@tweenjs/tween.js';
import { PlaySound } from 'System/Audio/AudioHelper';
import { PopupID } from 'State/UI/PopupID';
import RelativePositionComponent from 'Component/Render/RelativePosition';
import { Platform } from 'Utility/Platform';
import Point from 'Utility/Point';
import { WorldDataHelper } from 'Utility/WorldDataHelper';
import SpriteComponent from 'Component/Render/Sprite';
import { EventStateHelper } from 'State/Events/EventStateHelper';
import PIXIRenderer from 'System/Render/PIXIRenderer';

import getEventLayout = EventStateHelper.getEventLayout;

export class LevelCompletePopup extends BaseMenu {
    private layout: any;
    private sideMenu: SideMenu;
    private levelNumber: number;
    private levelReward: number;
    private maxStarsEarned: boolean;

    constructor(uiSystem: BaseUISystem) {
        super(PopupID.LEVEL_COMPLETE_POPUP, uiSystem, false);
    }

    public onShow() {
        super.onShow();

        const state = Game.store.getState();
        const { completeData } = state.levelState;
        this.levelNumber = completeData.levelNumber;
        this.levelReward = completeData.coins;
        this.maxStarsEarned = completeData.stars === 3;

        this.layout = getEventLayout(state, PopupID.LEVEL_COMPLETE_POPUP) ?? new LevelCompletePopupLayout();
        this.layout.root.addComponent(RelativePositionComponent);
        this.layout.root.getComponent(RelativePositionComponent).relativePosition = new Point(0.5, 0.5);
        this.element.addChild(this.layout.root);

        this.layout.levelText.getComponent(TextComponent).text = Game.locale
            .getText('LevelXComplete')
            .replace('[x]', String(this.levelNumber));

        this.layout.levelReward.getComponent(TextComponent).text = String(this.levelReward);
        this.layout.stackReward.getComponent(TextComponent).text = String(state.levelState.completeData.stackReward);
        this.layout.character.addComponent(RelativePositionComponent).y = 0;

        const totalReward = this.levelReward + state.levelState.completeData.stackReward;
        this.layout.totalReward.getComponent(TextComponent).text = String(totalReward);

        this.configureSideMenuForNative();

        if (WorldDataHelper.isHardLevel(this.levelNumber)) {
            this.layout.starPlaces.forEach((starPlace: any, i: number) => {
                const transform = starPlace.getComponent(TransformComponent);
                starPlace.getComponent(SpriteComponent).texture = 'star_base_hard';
                transform.scale.set(1.4);
                transform.x += [2, 0, -2][i];
                transform.y += 10;
            });

            this.layout.stars.forEach((star: any) => {
                star.getComponent(SpriteComponent).texture = 'star_hard';
            });

            this.layout.headerBg.getComponent(SpriteComponent).texture = 'header_hard';
            this.layout.headerBg.getComponent(TransformComponent).y += 16;
            this.layout.closeButton.getComponent(SpriteComponent).texture = 'x_button_hard';
            this.layout.bg.getComponent(SpriteComponent).texture = 'popup_background_hard';
            this.layout.levelText.getComponent(TextComponent).style.stroke = '#781F9D';
        }

        [this.layout.playButton, this.layout.closeButton].forEach(button => applyDefaultButtonAnimation(button));

        this.layout.playButton.on(GameObjectEvent.SELECTED, this.handlePlayClick);
        this.layout.closeButton.on(GameObjectEvent.SELECTED, this.handleCloseClick);

        this.updateStars(completeData.stars);
    }

    public onDestroy(): void {
        super.onDestroy();
    }

    protected onShown(params?: any): void {
        super.onShown(params);
        trackLevelEndShown({ levelNumber: this.levelNumber });
    }

    private setInputEnabled(value: boolean): void {
        this.layout.root.container.interactiveChildren = value;
    }

    private readonly handleCloseClick = (): void => {
        this.setInputEnabled(false);
        this.continue({ status: LevelStatus.EXITING });
    };

    private readonly handlePlayClick = (): void => {
        this.setInputEnabled(false);
        this.continue({ status: LevelStatus.COMPLETE });
    };

    private continue(props: { status: LevelStatus.COMPLETE | LevelStatus.EXITING }): void {
        const state: IGameState = Game.store.getState();
        const firstComplete = state.levelState.completeData?.firstComplete;
        trackFtuxOnHide({ levelNumber: this.levelNumber });

        GameActions.endLevel({ status: props.status, firstComplete });
    }

    private updateStars(starsAmount: number): void {
        const sounds = ['3-stars-1', '3-stars-2', '3-stars-3'];

        this.layout.stars.map(async (star: any, i: number) => {
            const visible = i < starsAmount;
            star.getComponent(Renderable).visible = visible;

            if (visible) {
                const transform = star.getComponent(TransformComponent);
                const targetScale = WorldDataHelper.isHardLevel(this.levelNumber) ? 1.4 : 1;
                transform.scale.set(0);

                await sleep(500 + i * 350);
                Anim(star, { scaleX: targetScale, scaleY: targetScale, duration: 450, easing: Easing.Back.Out });
                await sleep(300);
                PlaySound(sounds[i]);
            }
        });
    }

    private configureSideMenuForNative() {
        if (Game.config.platform === Platform.ANDROID || Game.config.platform === Platform.IOS) {
            return;
        }

        this.sideMenu = new SideMenu({ levelNumber: this.levelNumber });
        this.layout.sideContainer.addChild(this.sideMenu.root);
    }

    protected resize(screen: { CANVAS_WIDTH: number; CANVAS_HEIGHT: number }): void {
        super.resize(screen);
        if (this.element) {
            this.element.container.resize(PIXIRenderer.renderer.width, PIXIRenderer.renderer.height);
        }
    }
}
